import {
  applicationOperationsMonthLabel,
  isApplicationOperationsProduct,
} from './application-operations.js';
import { lineTotal } from './money.js';

/**
 * Subscriptions ("Abonnementer"): the fixed fees for running a customer's application or app,
 * billed in advance on the same invoices as application operations - the draft that closes a
 * month, or a manual invoice created on the 1st. See application-operations.ts.
 *
 * Two sources (decided 07-10-2026):
 *
 *   CRM        a won contract with an `Applikations drift - …` or `App - …` product line. Billed
 *              every month, priced by the BC item with the same name.
 *   Codepass   a subscription created on /fakturering/abonnementer and kept in
 *              `codepass_subscriptions`: a customer, a BC item, a price per month, the month it
 *              starts and how often it is billed.
 *
 * A Codepass subscription is billed on the invoice that bills the first month of each period:
 * every month, every third month from the start, or every twelfth. The line is the price per
 * month times the months in the period - a quarter is 3x the price, a year 12x.
 *
 * A subscription can be bound to one of the customer's contracts, but need not be. Its invoice
 * line then carries the contract, like a CRM line.
 *
 * **It replaces a CRM line for the same product** from its first month, so a subscription made
 * here to bill quarterly does not come on top of the monthly line in CRM: the line on its own
 * contract when it is bound to one, otherwise the line on any of the customer's contracts.
 *
 * A period is billed by the invoice for its first month only. A subscription created after that
 * invoice was made starts paying with its next period - nothing is billed backwards.
 */

export const SUBSCRIPTION_INTERVALS = ['monthly', 'quarterly', 'yearly'] as const;
export type SubscriptionInterval = (typeof SUBSCRIPTION_INTERVALS)[number];

/** How many months one invoice bills. */
export const SUBSCRIPTION_INTERVAL_MONTHS: Record<SubscriptionInterval, number> = {
  monthly: 1,
  quarterly: 3,
  yearly: 12,
};

export type SubscriptionKind = 'application_operations' | 'app';
export type SubscriptionSource = 'codepass' | 'crm';

/** `2026-11`. */
export const ISO_MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * A CRM product or BC item that is an app: `App - Venue Vibes`. Trimmed, any case. Not
 * `Applikations drift`, which is its own kind - the word boundary keeps the two apart.
 */
export function isAppProduct(name: string | null | undefined): boolean {
  return /^app\b/i.test(name?.trim() ?? '');
}

export function subscriptionKind(name: string | null | undefined): SubscriptionKind | null {
  if (isApplicationOperationsProduct(name)) return 'application_operations';
  if (isAppProduct(name)) return 'app';
  return null;
}

/** A product billed as a subscription - application operations or an app. */
export function isSubscriptionProduct(name: string | null | undefined): boolean {
  return subscriptionKind(name) !== null;
}

/** Months since year 0, so two months can be subtracted. */
function monthNumber(month: string): number {
  const [year, monthNo] = month.split('-').map(Number);
  return (year ?? Number.NaN) * 12 + (monthNo ?? Number.NaN) - 1;
}

/** `2026-11` plus 3 is `2027-02`. Counted on the numbers - no `Date`, no time zone. */
export function addMonthsToMonth(month: string, months: number): string {
  const total = monthNumber(month) + months;
  const year = Math.floor(total / 12);
  return `${year}-${String(total - year * 12 + 1).padStart(2, '0')}`;
}

/** Negative when `a` is before `b`. */
export function compareMonths(a: string, b: string): number {
  return monthNumber(a) - monthNumber(b);
}

/** The last day of the month: `2027-02` gives `2027-02-28`. */
export function lastDayOfMonth(month: string): string {
  const [year, monthNo] = month.split('-').map(Number);
  const day = new Date(Date.UTC(year ?? Number.NaN, monthNo ?? Number.NaN, 0)).getUTCDate();
  return `${month}-${String(day).padStart(2, '0')}`;
}

/**
 * The month the next invoice bills subscriptions for: on the 1st it is this month - the
 * automatic draft and a manual invoice made that day bill it - on any other day the next one.
 */
export function nextBillingMonth(today: string): string {
  const month = today.slice(0, 7);
  return today.slice(8, 10) === '01' ? month : addMonthsToMonth(month, 1);
}

/** What decides when a Codepass subscription is billed. */
export interface SubscriptionTerms {
  interval: SubscriptionInterval;
  /** The first month, `YYYY-MM`. */
  start_month: string;
  /** The last month paid for, `YYYY-MM`, or null while it runs. */
  end_month: string | null;
}

/** One period: its first month and how many months it bills. */
export interface SubscriptionPeriod {
  month: string;
  months: number;
}

/**
 * The period that begins in `month`, or null when none does.
 *
 * The last period stops at `end_month`: a quarterly subscription that ends a month into a quarter
 * bills that one month, not three.
 */
export function subscriptionPeriodAt(
  terms: SubscriptionTerms,
  month: string,
): SubscriptionPeriod | null {
  const length = SUBSCRIPTION_INTERVAL_MONTHS[terms.interval];
  const offset = compareMonths(month, terms.start_month);
  if (offset < 0 || offset % length !== 0) return null;
  if (terms.end_month && compareMonths(month, terms.end_month) > 0) return null;

  const left = terms.end_month ? compareMonths(terms.end_month, month) + 1 : length;
  return { month, months: Math.min(length, left) };
}

/** The first period that begins in `from` or later, or null when the subscription ends before. */
export function nextSubscriptionPeriod(
  terms: SubscriptionTerms,
  from: string,
): SubscriptionPeriod | null {
  const length = SUBSCRIPTION_INTERVAL_MONTHS[terms.interval];
  const offset = compareMonths(from, terms.start_month);
  const steps = offset <= 0 ? 0 : Math.ceil(offset / length);
  return subscriptionPeriodAt(terms, addMonthsToMonth(terms.start_month, steps * length));
}

/** `november 2026`, or `november 2026 – januar 2027` for a period of more than one month. */
export function subscriptionPeriodLabel(month: string, months: number): string {
  const first = applicationOperationsMonthLabel(month);
  if (months <= 1) return first;
  return `${first} – ${applicationOperationsMonthLabel(addMonthsToMonth(month, months - 1))}`;
}

/** The customer-facing line text: `App - Venue Vibes, november 2026 – januar 2027`. */
export function subscriptionDescription(product: string, month: string, months: number): string {
  return `${product.trim()}, ${subscriptionPeriodLabel(month, months)}`;
}

/** What a period costs: the price per month times the months. */
export function subscriptionPeriodAmount(monthlyPriceExclVat: number, months: number): number {
  return lineTotal(months, monthlyPriceExclVat);
}

/**
 * The key a billed period of a Codepass subscription is remembered under - the subscription and
 * the period's first month. Never equal to an `applicationOperationsKey`, which is a CRM contract.
 */
export function subscriptionKey(subscriptionId: string, month: string): string {
  return `subscription:${subscriptionId.trim().toLowerCase()}|${month}`;
}

/** What decides which CRM lines a Codepass subscription replaces. */
export interface ReplacingSubscription {
  product: string;
  start_month: string;
  /** The contract it is bound to, or null for none. */
  contract_id: string | null;
}

/**
 * The month a CRM line is replaced from, or null when nothing replaces it: the earliest start
 * among the subscriptions on the same product that are bound to the line's contract - or to no
 * contract, which then covers the customer's lines on all contracts.
 *
 * The subscriptions must already be the line's customer's. A subscription that has ended keeps
 * the line replaced: the customer has left the product, and the CRM line must not start billing
 * again. One line, one answer - the invoices, the list and the rule `applikationsdrift` all ask
 * here.
 */
export function crmLineReplacedFrom(
  subscriptions: readonly ReplacingSubscription[],
  line: { contract_id: string; product: string },
): string | null {
  const product = line.product.trim().toLowerCase();
  const contract = line.contract_id.trim().toLowerCase();
  let from: string | null = null;

  for (const subscription of subscriptions) {
    if (subscription.product.trim().toLowerCase() !== product) continue;
    if (subscription.contract_id && subscription.contract_id.trim().toLowerCase() !== contract) {
      continue;
    }
    if (from === null || compareMonths(subscription.start_month, from) < 0) {
      from = subscription.start_month;
    }
  }
  return from;
}

/** A CRM contract, as a subscription names it. */
export interface SubscriptionContract {
  id: string;
  contract_no: string | null;
  title: string;
}

/** GET /subscriptions/contracts - the customer's won contracts a subscription can be bound to. */
export interface SubscriptionContractsResponse {
  contracts: SubscriptionContract[];
}

/** One row on /fakturering/abonnementer - a Codepass subscription, or a line on a CRM contract. */
export interface SubscriptionRow {
  /** `codepass:<id>` or `crm:<line id>` - unique in the list. */
  key: string;
  source: SubscriptionSource;
  kind: SubscriptionKind;
  /** The customer in Business Central. `id` is null when a CRM account has no match there. */
  customer: { id: string | null; name: string; cvr: string | null };
  product: string;
  /** The quantity on the CRM line; 1 on a Codepass subscription. */
  quantity: number;
  /** What one month costs. Null when the price cannot be found in Business Central. */
  monthly_excl_vat: number | null;
  interval: SubscriptionInterval;
  /** The first month - on a CRM line, the month the contract was signed. */
  start_month: string | null;
  /** The last month paid for, or null while it runs. */
  end_month: string | null;
  /** The next period to bill and what it costs. Null when nothing more is billed. */
  next: { month: string; months: number; amount_excl_vat: number | null } | null;
  /**
   * The CRM contract: the one the line is on, or the one a Codepass subscription is bound to.
   * Null on a Codepass subscription without one - the contract is optional there.
   */
  contract: SubscriptionContract | null;
  /** Null on a CRM line. */
  subscription_id: string | null;
  /** On a CRM line: the month a Codepass subscription for the same product takes over. */
  replaced_from: string | null;
  /** Why it cannot be billed as it stands, in Danish - or null. */
  problem: string | null;
  created_by_name: string | null;
  created_at: string | null;
}

/** A BC item a subscription can be created on. */
export interface SubscriptionItem {
  id: string;
  number: string | null;
  name: string;
  kind: SubscriptionKind;
  unit_price_excl_vat: number;
}

/** GET /subscriptions. */
export interface SubscriptionOverview {
  /** False until `codepass_subscriptions` exists - see docs/subscriptions-setup.md. */
  ready: boolean;
  /** The month the next invoice bills, `YYYY-MM` - see `nextBillingMonth`. */
  next_month: string;
  rows: SubscriptionRow[];
  /** The BC items a subscription can be created on: application operations and apps. */
  items: SubscriptionItem[];
  /** A source that could not be read, so the list may be short. Danish. */
  notes: string[];
}

/** POST /subscriptions. */
export interface CreateSubscriptionRequest {
  /** The BC customer's id. */
  customer_id: string;
  /** One of the customer's won contracts in CRM, or null for none - the contract is optional. */
  contract_id: string | null;
  bc_item_id: string;
  monthly_price_excl_vat: number;
  interval: SubscriptionInterval;
  /** `YYYY-MM`. */
  start_month: string;
}

/**
 * POST /subscriptions/overwrite - new terms for a running row, from `start_month` on. The new
 * terms are saved as a Codepass subscription on the same customer, contract and product; see
 * `overwriteSubscription` in the API.
 */
export interface OverwriteSubscriptionRequest {
  /** The row's source. `id` is then the CRM line's id or the subscription's. */
  source: SubscriptionSource;
  id: string;
  monthly_price_excl_vat: number;
  interval: SubscriptionInterval;
  /** `YYYY-MM` - no earlier than the first month not billed yet. */
  start_month: string;
}

/** POST /subscriptions/:id/end - the last month the customer pays for. */
export interface EndSubscriptionRequest {
  end_month: string;
}
